/**
 * Language + copy for /new.
 *
 * Every user-facing string on this surface lives here, in both languages. This
 * file used to hold four (two placeholders and the starter chips) while the
 * other ~fifty were hardcoded English, which produced the worst possible
 * result: an English page with Indonesian placeholders inside it. Half a
 * translation reads as a bug, not as localisation.
 *
 * HOW THE LANGUAGE IS CHOSEN, in order:
 *   1. An explicit choice from the toggle (persisted, and always wins).
 *   2. The browser's language list. Any `id-*` means Indonesian. Any OTHER
 *      stated language means English — deliberately: a tourist or expat on an
 *      en-US browser in Jakarta must not be forced into Bahasa. The toggle is
 *      how an Indonesian creator on an English phone gets Bahasa, and it
 *      sticks.
 *   3. Only when the browser states nothing at all: the studio's geo endpoint
 *      (/api/geo → x-vercel-ip-country / cf-ipcountry), then the IANA timezone
 *      as a last resort.
 *
 * Geo is deliberately NOT allowed to override a stated browser language: an IP
 * is a guess about where a device is, a language list is a statement about who
 * is holding it.
 */

import { useSyncExternalStore } from 'react'

export type Lang = 'en' | 'id'

const LANG_KEY = 'db.newLang'
const ID_TIMEZONES = new Set(['Asia/Jakarta', 'Asia/Pontianak', 'Asia/Makassar', 'Asia/Jayapura'])
const API_BASE = (): string => process.env.VITE_API_URL || 'https://dreamboothstudio.com'

// --- language resolution ----------------------------------------------------

const listeners = new Set<() => void>()
let geoLang: Lang | null = null
let geoRequested = false

function readOverride(): Lang | null {
  try {
    const v = window.localStorage.getItem(LANG_KEY)
    return v === 'id' || v === 'en' ? v : null
  } catch {
    return null
  }
}

let override: Lang | null = typeof window === 'undefined' ? null : readOverride()

function emit(): void {
  listeners.forEach((l) => l())
}

/** The language the browser itself states, or null when it states nothing. */
function browserPreference(): Lang | null {
  try {
    const langs = [
      navigator.language,
      ...(Array.isArray(navigator.languages) ? navigator.languages : [])
    ]
      .map((l) => (l || '').toLowerCase())
      .filter(Boolean)
    if (langs.some((l) => l.startsWith('id'))) return 'id'
    if (langs.length > 0) return 'en'
  } catch {
    // Non-browser / locked-down env.
  }
  return null
}

function timezonePreference(): Lang | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (tz && ID_TIMEZONES.has(tz)) return 'id'
  } catch {
    // ignore
  }
  return null
}

/** Current language. Cheap and synchronous — safe to call during render. */
export function currentLang(): Lang {
  return override ?? browserPreference() ?? geoLang ?? timezonePreference() ?? 'en'
}

export function setLang(lang: Lang): void {
  override = lang
  try {
    window.localStorage.setItem(LANG_KEY, lang)
  } catch {
    // Storage blocked — the choice still holds for this session.
  }
  emit()
}

/**
 * Asks the studio where the visitor is — once, and only when it can actually
 * change the answer (no override, and a browser that stated nothing).
 * Fail-open: any error leaves the timezone fallback in charge.
 */
function requestGeo(): void {
  if (geoRequested) return
  geoRequested = true
  if (override || browserPreference()) return
  void fetch(`${API_BASE()}/api/geo`, { credentials: 'omit', cache: 'no-cache' })
    .then((res) => (res.ok ? res.json() : null))
    .then((data: { suggestedLocale?: string | null } | null) => {
      if (data?.suggestedLocale !== 'id') return
      geoLang = 'id'
      emit()
    })
    .catch(() => {
      /* geo is best-effort */
    })
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  requestGeo()
  return () => {
    listeners.delete(cb)
  }
}

/** Subscribes a component to the current language. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, currentLang, () => 'en' as Lang)
}

/** True when the visitor is (best-effort) in / using Indonesia. */
export function isIndonesiaRegion(): boolean {
  return currentLang() === 'id'
}

/** BCP-47-ish locale to send to the generator: 'id' in Indonesia, else 'en'. */
export function generationLocale(): string {
  return currentLang()
}

// --- the dictionary ---------------------------------------------------------

export interface Copy {
  // Shell
  documentTitle: string
  stepLook: string
  stepName: string
  stepStrip: string
  stepColour: string
  stepEffect: string
  editPrompt: string
  editPromptHint: string
  startOver: string
  startOverHint: string
  startOverConfirmBooth: string
  startOverConfirmPrompt: string
  restoredBooth: string
  restoredPrompt: string
  dismiss: string
  back: string
  continueLabel: string
  saveAndGoLive: string
  continueWithGoogle: string
  creating: string
  signInNotCompleted: string
  slugJustTaken: string
  signInExpired: string
  somethingWentWrong: string
  generationFailed: string
  generationTimedOut: string
  regenerationFailed: string
  theaterSlow: string

  // Navbar
  dashboard: string
  learnMore: string
  signIn: string

  // Composer
  heroSub: string
  placeholder: string
  starterPrompts: string[]
  addReference: string
  attachLogo: string
  removeLogo: string
  removeReference: string
  roleAuto: string
  roleStyle: string
  roleLiteral: string
  roleHint: string
  errNotImage: string
  errReferenceTooBig: string
  errLogoTooBig: string
  preparing: string
  buildIt: string
  buildItSignedOut: string
  signInToBuild: string
  replaceMyDesign: string
  pickAVibe: string
  backToMyBooth: string
  rebuildWarning: string
  rebuildLeft: (n: number) => string
  rebuildKeeps: string

  // Clarify
  quickQuestions: string
  quickQuestionsSub: string
  other: string
  otherPlaceholder: string
  skipJustGenerate: string
  buildWithAnswers: (n: number) => string

  // Look
  welcomeDesign: string
  welcomeDesignSub: string
  adjustmentLabel: string
  adjustmentPlaceholder: string
  redrawWelcome: string
  welcomeDesignMissing: string
  appBackground: string
  appBackgroundSub: string
  reroll: string
  regensLeft: (n: number) => string
  redoEverything: (n: number) => string

  // Name & link
  photoboothName: string
  boothLink: string
  slugAvailable: string
  slugTaken: string
  slugInvalid: string
  sessionStyle: string
  aiSuggested: (mode: string) => string
  classicStrip: string
  classicStripSub: string
  frameMode: string
  frameModeSub: string

  // Link ingest
  linkLabel: string
  linkPlaceholder: string
  linkRead: string
  linkReading: string
  linkFound: (n: number) => string
  linkPrivate: string
  linkUnreadable: string

  // Frames
  drawingFrames: string
  frames: string
  framesSub: string
  nSelected: (n: number) => string
  searchFrames: string
  designYourOwn: string
  signInRequired: string
  yours: string
  noFramesMatch: (q: string) => string
  noFrames: string
  loadingMoreFrames: string
  starterFramesNote: (n: number) => string

  // Frame modal
  yourOwnFrame: string
  modalLayout: string
  modalDescribe: string
  modalPreview: string
  pickPhotoCount: string
  noLayouts: string
  describeFrame: string
  framePlaceholder: string
  editFramePlaceholder: string
  drawIt: string
  applyEdit: string
  addToBooth: string
  cancel: string
  photosCount: (n: number) => string
  frameGenerationFailed: string
  frameEditFailed: string
  frameSaveFailed: string

  // Filters
  filters: string
  filtersSub: string
  searchFilters: string
  tryIt: string
  tryItHint: string
  uploadTestPhoto: string
  usingYourPhoto: string
  removeTestPhoto: string
  dreamboothPicks: string
  community: string
  noFiltersMatch: (q: string) => string

  // Effects
  aiEffect: string
  aiEffectSub: string
  searchEffects: string

  // Finish + theater + gallery
  copyLink: string
  copied: string
  openMyBooth: string
  yourBooths: string
  openBooth: string
  claimThisLink: string
  yourBoothLink: string
  slugPlaceholder: string
  takePhoto: string
  stageReading: string
  stagePalette: string
  stageLayout: string
  stagePainting: string
  stagePicking: string
  stageChecking: string
}

const EN: Copy = {
  documentTitle: 'Create your photobooth · Dreambooth',
  stepLook: 'Look',
  stepName: 'Name & link',
  stepStrip: 'Strip',
  stepColour: 'Colour',
  stepEffect: 'AI moment',
  editPrompt: 'Edit prompt',
  editPromptHint: 'Describe the booth again and rebuild the design',
  startOver: 'Start over',
  startOverHint: 'Delete this booth and start from a new description',
  startOverConfirmBooth:
    'Start over? Your booth design, name, link and selections are deleted. This cannot be undone.',
  startOverConfirmPrompt: 'Clear what you have written and start over?',
  restoredBooth: 'Picked up where you left off — your booth was saved on this device.',
  restoredPrompt: 'Picked up where you left off — your description was saved on this device.',
  dismiss: 'Dismiss',
  back: 'Back',
  continueLabel: 'Continue',
  saveAndGoLive: 'Save & go live',
  continueWithGoogle: 'Continue with Google',
  creating: 'Creating…',
  signInNotCompleted: 'Sign-in was not completed. Your booth is saved — try again.',
  slugJustTaken: 'That booth link just got taken — pick another.',
  signInExpired: 'Sign-in expired. Tap create again to continue with Google.',
  somethingWentWrong: 'Something went wrong. Try again.',
  generationFailed: 'Generation failed. Try again.',
  generationTimedOut:
    'That took longer than expected and we stopped waiting. Your description is still here — try again.',
  regenerationFailed: 'Regeneration failed.',
  theaterSlow: 'Still working — a detailed design can take a couple of minutes.',

  dashboard: 'Dashboard',
  learnMore: 'Learn more',
  signIn: 'Sign in',

  heroSub:
    'A photobooth your guests open on their phone. Describe it, we design it, you share one link.',
  placeholder:
    'Describe your dream photobooth… e.g. “Scrapbook 21st birthday, cute stickers, pastel colors”',
  starterPrompts: [
    'Scrapbook birthday, cute stickers & doodles, pastel',
    'Y2K chrome & pixel, candy pop colors',
    'Vintage magazine cover, bold retro',
    'Vintage newspaper, classic black & white',
    'Sweet sixteen pastel pink, playful & girly',
    'Corporate gathering, navy & gold, professional',
    'Anniversary dinner, warm candlelight, romantic'
  ],
  addReference: 'Add reference',
  attachLogo: 'Attach logo',
  removeLogo: 'Remove logo',
  removeReference: 'Remove reference',
  roleAuto: 'Auto',
  roleStyle: 'Style ref',
  roleLiteral: 'Use in design',
  roleHint: 'Tap to change how the AI uses this image',
  errNotImage: 'Attach an image file (PNG/JPG).',
  errReferenceTooBig: 'Reference images must be under 2MB.',
  errLogoTooBig: 'Logo must be under 2MB.',
  preparing: 'Preparing…',
  buildIt: 'Build it',
  buildItSignedOut: 'Sign in & build it',
  signInToBuild: 'Sign in with Google first — two clicks, then your booth builds.',
  replaceMyDesign: 'Replace my design',
  pickAVibe: 'Optional — pick a vibe',
  backToMyBooth: 'Back to my booth',
  rebuildWarning: 'Building again replaces your current design.',
  rebuildLeft: (n) => `${n} of 3 ${n === 1 ? 'rebuild' : 'rebuilds'} left.`,
  rebuildKeeps: 'Your link and your frame & colour picks are kept.',

  quickQuestions: 'Quick questions',
  quickQuestionsSub: 'Optional — a few answers make the design a lot more yours.',
  other: 'Other…',
  otherPlaceholder: 'Tell us in your own words',
  skipJustGenerate: 'Skip — just generate',
  buildWithAnswers: (n) => `Build with ${n} answer${n > 1 ? 's' : ''}`,

  welcomeDesign: 'Welcome design',
  welcomeDesignSub:
    'Tell the AI what to change — it keeps the design and changes only that. Mention “phone” or “laptop” to target one screen; otherwise both update together.',
  adjustmentLabel: 'Adjustment (optional)',
  adjustmentPlaceholder: 'e.g. “make the font handwritten, bigger title on laptop”…',
  redrawWelcome: 'Redraw the welcome screen',
  welcomeDesignMissing:
    'The welcome screen didn’t come out this time. Redraw it, or adjust your prompt and build again.',
  appBackground: 'App background',
  appBackgroundSub: 'A calmer companion backdrop behind the rest of the booth.',
  reroll: 'Reroll',
  regensLeft: (n) => `${n} regeneration${n === 1 ? '' : 's'} left`,
  redoEverything: (n) => `redo everything from a new prompt (${n} left)`,

  photoboothName: 'Photobooth name',
  boothLink: 'Booth link',
  slugAvailable: 'Available — this link is yours.',
  slugTaken: 'Taken. Try another.',
  slugInvalid: 'Use 3+ lowercase letters, numbers and hyphens.',
  sessionStyle: 'Session style',
  aiSuggested: (mode) => `AI suggested ${mode} — you can flip it.`,
  classicStrip: 'Classic strip',
  classicStripSub:
    'Photo-by-photo with countdown, retakes, and a photo-strip result. Best on phones.',
  frameMode: 'Frame mode',
  frameModeSub: 'Every shot lands straight into one composed frame — single-take, poster-like.',

  linkLabel: 'Or start from a link',
  linkPlaceholder: 'instagram.com/… or any website',
  linkRead: 'Read it',
  linkReading: 'Reading…',
  linkFound: (n) => `Got it — added ${n} reference${n === 1 ? '' : 's'} from that page.`,
  linkPrivate:
    "That page needs a login, so we can't read it. Attach a few screenshots instead — or just describe it and we'll ask a couple of quick questions.",
  linkUnreadable: "We couldn't read that link. Check it, or describe the vibe instead.",

  drawingFrames: 'Designing frames for your booth…',
  frames: 'Frames',
  framesSub: 'Most-loved templates first — guests choose from these at capture.',
  nSelected: (n) => `${n} selected`,
  searchFrames: 'Search frames…',
  designYourOwn: 'Design your own',
  signInRequired: 'Sign-in required',
  yours: 'Yours',
  noFramesMatch: (q) => `No frames match “${q}”.`,
  noFrames: 'No frame templates available.',
  loadingMoreFrames: 'Loading more frames…',
  starterFramesNote: (n) => `We picked ${n} to get you started — tap any frame to swap them.`,

  yourOwnFrame: 'Your own frame',
  modalLayout: 'Layout',
  modalDescribe: 'Describe',
  modalPreview: 'Preview',
  pickPhotoCount: 'Pick how many photos the frame holds — the AI decorates around them.',
  noLayouts: 'No layouts available right now.',
  describeFrame:
    'Describe the frame — style, colors, motifs, occasion. The photo windows stay exactly where the layout puts them.',
  framePlaceholder: 'e.g. “Y2K scrapbook, chrome stickers, hand doodles, candy pop colors”',
  editFramePlaceholder: 'Change something? e.g. “make the flowers gold, less busy at the top”',
  drawIt: 'Draw it',
  applyEdit: 'Apply edit',
  addToBooth: 'Add to booth',
  cancel: 'Cancel',
  photosCount: (n) => `${n} photo${n === 1 ? '' : 's'}`,
  frameGenerationFailed: 'Generation failed. Try again.',
  frameEditFailed: 'Edit failed. Try again.',
  frameSaveFailed: 'Could not save the frame.',

  filters: 'Filters',
  filtersSub: 'Guests pick one at capture.',
  searchFilters: 'Search filters…',
  tryIt: 'Try it',
  tryItHint: 'Take a test photo — every filter previews on it',
  uploadTestPhoto: 'Upload a test photo',
  usingYourPhoto: 'Using your photo',
  removeTestPhoto: 'Remove test photo',
  dreamboothPicks: 'Dreambooth picks',
  community: 'Community',
  noFiltersMatch: (q) => `No filters match “${q}”.`,

  aiEffect: 'AI effect (optional)',
  aiEffectSub:
    'Guests can restyle their photos with AI after each session. Create your own later from the dashboard.',
  searchEffects: 'Search effects…',

  copyLink: 'Copy link',
  copied: 'Copied',
  openMyBooth: 'Open my booth',
  yourBooths: 'Your booths',
  openBooth: 'Open booth',
  claimThisLink: 'Claim this link',
  yourBoothLink: 'Your booth link',
  slugPlaceholder: 'your-party',
  takePhoto: 'Take photo',
  stageReading: 'Reading your description',
  stagePalette: 'Choosing your palette',
  stageLayout: 'Laying out the welcome screen',
  stagePainting: 'Painting the backdrop',
  stagePicking: 'Picking frames and filters',
  stageChecking: 'Checking it on phone and laptop'
}

const ID: Copy = {
  documentTitle: 'Bikin photobooth kamu · Dreambooth',
  stepLook: 'Tampilan',
  stepName: 'Nama & link',
  stepStrip: 'Strip',
  stepColour: 'Warna',
  stepEffect: 'Momen AI',
  editPrompt: 'Ubah prompt',
  editPromptHint: 'Ceritakan ulang boothnya dan bikin desainnya dari awal',
  startOver: 'Mulai Ulang',
  startOverHint: 'Hapus booth ini dan mulai dari deskripsi baru',
  startOverConfirmBooth:
    'Mulai ulang? Desain, nama, link, dan pilihan booth kamu akan dihapus. Tidak bisa dibatalkan.',
  startOverConfirmPrompt: 'Hapus yang sudah kamu tulis dan mulai ulang?',
  restoredBooth: 'Dilanjutkan dari terakhir — booth kamu tersimpan di perangkat ini.',
  restoredPrompt: 'Dilanjutkan dari terakhir — deskripsi kamu tersimpan di perangkat ini.',
  dismiss: 'Tutup',
  back: 'Kembali',
  continueLabel: 'Selanjutnya',
  saveAndGoLive: 'Simpan & aktifkan',
  continueWithGoogle: 'Lanjut dengan Google',
  creating: 'Membuat…',
  signInNotCompleted: 'Login belum selesai. Booth kamu tersimpan — coba lagi.',
  slugJustTaken: 'Link booth ini baru saja diambil orang lain — pilih yang lain.',
  signInExpired: 'Sesi login habis. Tekan simpan lagi untuk lanjut dengan Google.',
  somethingWentWrong: 'Ada yang tidak beres. Coba lagi.',
  generationFailed: 'Gagal membuat desain. Coba lagi.',
  generationTimedOut:
    'Terlalu lama, jadi kami berhenti menunggu. Deskripsi kamu masih tersimpan — coba lagi.',
  regenerationFailed: 'Gagal membuat ulang.',
  theaterSlow: 'Masih dikerjakan — desain yang detail bisa makan waktu beberapa menit.',

  dashboard: 'Dashboard',
  learnMore: 'Selengkapnya',
  signIn: 'Masuk',

  heroSub:
    'Photobooth yang dibuka tamu kamu lewat HP. Ceritakan, kami desainkan, kamu tinggal bagikan satu link.',
  placeholder:
    'Ceritakan photobooth impianmu… e.g. “Ulang tahun ke-17 tema scrapbook, stiker lucu, warna pastel”',
  starterPrompts: [
    'Scrapbook birthday, stiker & doodle lucu, pastel',
    'Y2K chrome & pixel, warna candy pop',
    'Sampul majalah vintage, bold retro',
    'Koran vintage, hitam putih klasik',
    'Sweet seventeen pastel pink, playful & girly',
    'Corporate gathering, navy & gold, professional',
    'Anniversary dinner, candlelight warm, romantic'
  ],
  addReference: 'Tambah referensi',
  attachLogo: 'Pasang logo',
  removeLogo: 'Hapus logo',
  removeReference: 'Hapus referensi',
  roleAuto: 'Otomatis',
  roleStyle: 'Referensi gaya',
  roleLiteral: 'Pakai di desain',
  roleHint: 'Ketuk untuk mengubah cara AI memakai gambar ini',
  errNotImage: 'Lampirkan file gambar (PNG/JPG).',
  errReferenceTooBig: 'Gambar referensi maksimal 2MB.',
  errLogoTooBig: 'Logo maksimal 2MB.',
  preparing: 'Menyiapkan…',
  buildIt: 'Buatkan',
  buildItSignedOut: 'Login & buatkan',
  signInToBuild: 'Login dengan Google dulu — dua klik, lalu booth kamu langsung dibuat.',
  replaceMyDesign: 'Ganti desain saya',
  pickAVibe: 'Opsional — pilih vibe',
  backToMyBooth: 'Kembali ke booth saya',
  rebuildWarning: 'Membuat ulang akan mengganti desain kamu yang sekarang.',
  rebuildLeft: (n) => `Sisa ${n} dari 3 kali buat ulang.`,
  rebuildKeeps: 'Link, frame, dan pilihan warna kamu tetap aman.',

  quickQuestions: 'Beberapa pertanyaan',
  quickQuestionsSub: 'Opsional — beberapa jawaban bikin desainnya jauh lebih kamu banget.',
  other: 'Lainnya…',
  otherPlaceholder: 'Tulis dengan kata-kata kamu sendiri',
  skipJustGenerate: 'Lewati — langsung buat',
  buildWithAnswers: (n) => `Buat dengan ${n} jawaban`,

  welcomeDesign: 'Desain layar sambutan',
  welcomeDesignSub:
    'Bilang ke AI apa yang mau diubah — desainnya tetap, yang berubah cuma itu. Sebut “HP” atau “laptop” untuk satu layar saja; kalau tidak, keduanya ikut berubah.',
  adjustmentLabel: 'Penyesuaian (opsional)',
  adjustmentPlaceholder: 'e.g. “fontnya tulisan tangan, judul lebih besar di laptop”…',
  redrawWelcome: 'Gambar ulang layar sambutan',
  welcomeDesignMissing:
    'Layar sambutannya belum jadi kali ini. Gambar ulang, atau ubah prompt lalu buat lagi.',
  appBackground: 'Latar aplikasi',
  appBackgroundSub: 'Latar pendamping yang lebih kalem untuk halaman booth lainnya.',
  reroll: 'Acak lagi',
  regensLeft: (n) => `Sisa ${n} kali gambar ulang`,
  redoEverything: (n) => `ulangi semua dari prompt baru (sisa ${n})`,

  photoboothName: 'Nama photobooth',
  boothLink: 'Link booth',
  slugAvailable: 'Tersedia — link ini milik kamu.',
  slugTaken: 'Sudah dipakai. Coba yang lain.',
  slugInvalid: 'Pakai minimal 3 huruf kecil, angka, dan tanda hubung.',
  sessionStyle: 'Gaya sesi',
  aiSuggested: (mode) => `AI menyarankan ${mode} — bisa kamu ganti.`,
  classicStrip: 'Strip klasik',
  classicStripSub:
    'Foto satu per satu dengan hitung mundur, bisa diulang, hasilnya photo strip. Paling pas di HP.',
  frameMode: 'Mode frame',
  frameModeSub: 'Setiap jepretan langsung masuk ke satu frame utuh — sekali ambil, seperti poster.',

  linkLabel: 'Atau mulai dari sebuah link',
  linkPlaceholder: 'instagram.com/… atau website apa pun',
  linkRead: 'Baca',
  linkReading: 'Membaca…',
  linkFound: (n) => `Dapat — ${n} referensi dari halaman itu sudah ditambahkan.`,
  linkPrivate:
    'Halaman itu perlu login, jadi tidak bisa kami baca. Lampirkan beberapa screenshot saja — atau ceritakan saja, nanti kami tanya beberapa hal singkat.',
  linkUnreadable: 'Link itu tidak bisa kami baca. Cek lagi, atau ceritakan saja vibenya.',

  drawingFrames: 'Mendesain frame untuk booth kamu…',
  frames: 'Frame',
  framesSub: 'Yang paling disukai duluan — tamu memilih dari sini saat berfoto.',
  nSelected: (n) => `${n} dipilih`,
  searchFrames: 'Cari frame…',
  designYourOwn: 'Desain sendiri',
  signInRequired: 'Perlu masuk dulu',
  yours: 'Punyamu',
  noFramesMatch: (q) => `Tidak ada frame yang cocok dengan “${q}”.`,
  noFrames: 'Belum ada template frame.',
  loadingMoreFrames: 'Memuat frame lainnya…',
  starterFramesNote: (n) => `Kami pilihkan ${n} untuk mulai — ketuk frame mana saja untuk mengganti.`,

  yourOwnFrame: 'Frame kamu sendiri',
  modalLayout: 'Tata letak',
  modalDescribe: 'Deskripsi',
  modalPreview: 'Pratinjau',
  pickPhotoCount: 'Pilih berapa foto yang muat di frame — AI yang menghias sekelilingnya.',
  noLayouts: 'Belum ada tata letak yang tersedia.',
  describeFrame:
    'Ceritakan framenya — gaya, warna, motif, acaranya. Jendela fotonya tetap di posisi tata letak yang kamu pilih.',
  framePlaceholder: 'e.g. “Y2K scrapbook, stiker chrome, doodle tangan, warna candy pop”',
  editFramePlaceholder:
    'Mau ubah sesuatu? e.g. “bunganya jadi emas, bagian atas jangan terlalu ramai”',
  drawIt: 'Gambar',
  applyEdit: 'Terapkan',
  addToBooth: 'Tambahkan ke booth',
  cancel: 'Batal',
  photosCount: (n) => `${n} foto`,
  frameGenerationFailed: 'Gagal membuat frame. Coba lagi.',
  frameEditFailed: 'Gagal mengubah. Coba lagi.',
  frameSaveFailed: 'Frame tidak bisa disimpan.',

  filters: 'Filter',
  filtersSub: 'Tamu memilih satu saat berfoto.',
  searchFilters: 'Cari filter…',
  tryIt: 'Coba',
  tryItHint: 'Ambil foto uji — semua filter langsung dipratinjau di foto itu',
  uploadTestPhoto: 'Unggah foto uji',
  usingYourPhoto: 'Pakai fotomu',
  removeTestPhoto: 'Hapus foto uji',
  dreamboothPicks: 'Pilihan Dreambooth',
  community: 'Komunitas',
  noFiltersMatch: (q) => `Tidak ada filter yang cocok dengan “${q}”.`,

  aiEffect: 'Efek AI (opsional)',
  aiEffectSub:
    'Tamu bisa mengubah gaya fotonya pakai AI setelah sesi. Bikin efek sendiri nanti dari dashboard.',
  searchEffects: 'Cari efek…',

  copyLink: 'Salin link',
  copied: 'Tersalin',
  openMyBooth: 'Buka booth saya',
  yourBooths: 'Booth kamu',
  openBooth: 'Buka booth',
  claimThisLink: 'Ambil link ini',
  yourBoothLink: 'Link booth kamu',
  slugPlaceholder: 'pesta-kamu',
  takePhoto: 'Ambil foto',
  stageReading: 'Membaca deskripsi kamu',
  stagePalette: 'Memilih palet warna',
  stageLayout: 'Menyusun layar sambutan',
  stagePainting: 'Menggambar latarnya',
  stagePicking: 'Memilih frame dan filter',
  stageChecking: 'Mengecek di HP dan laptop'
}

const COPY: Record<Lang, Copy> = { en: EN, id: ID }

/** The full string table for the current language. Re-renders on a change. */
export function useCopy(): Copy {
  return COPY[useLang()]
}

/** Non-reactive read, for callbacks and non-component code. */
export function copy(): Copy {
  return COPY[currentLang()]
}
